import { ACTIVITY_GRAHQL_TYPES, ACTIVITY_RENDER_TYPES } from 'common/ActivityChip/const';
import { PROTECTION_ACTIVITY_TYPE, TRAVELLING_ACTIVITY_TYPE } from 'constants/activityTypes';

/**
 * Handler for Absence items.
 */
const handleAbsenceActions = (activity, callbacks) => {
	const actions = [
		{
			id: 'edit-absence',
			label: 'Edit Absence',

			action: () =>
				callbacks.onUpdateAbsence({
					id: activity.id,
					crewMemberId: activity.crewMemberId,
					reason: activity.reason?.id ?? '',
					fromTimestamp: activity.fromTimestamp,
					toTimestamp: activity.toTimestamp,
					note: activity.note ?? '',
					availableForAssignments: activity.availableForAssignments,
					isRRO:
						activity.renderType === ACTIVITY_RENDER_TYPES.rroTimeOffAvailabilityDisabled ||
						activity.renderType === ACTIVITY_RENDER_TYPES.rroTimeOffAvailabilityEnabled,
				}),
		},
		{
			id: 'delete-absence',
			label: 'Delete Absence',

			action: () => {
				const isRRO =
					activity.renderType === ACTIVITY_RENDER_TYPES.rroTimeOffAvailabilityDisabled ||
					activity.renderType === ACTIVITY_RENDER_TYPES.rroTimeOffAvailabilityEnabled;
				callbacks.onDeleteAbsence(activity.id, { isRRO, fromTimestamp: activity.fromTimestamp });
			},
		},
	];
	// Specific Logic: Regular Operator Time Off
	if (
		activity.renderType === ACTIVITY_RENDER_TYPES.rroTimeOffAvailabilityDisabled ||
		activity.renderType === ACTIVITY_RENDER_TYPES.rroTimeOffAvailabilityEnabled
	) {
		const { id, crewMemberId, reason, fromTimestamp, toTimestamp, availableForAssignments } =
			activity;

		actions.push({
			id: 'toggle-availability',
			label: availableForAssignments
				? 'Make unavailable for assignments'
				: 'Make available for assignments',
			action: () =>
				callbacks.onToggleAvailability({
					id,
					input: {
						crewMemberId,
						reasonId: reason.id,
						fromTimestamp,
						toTimestamp,
						availableForAssignments: !availableForAssignments,
					},
				}),
		});
	}

	return actions;
};

/**
 * Handler for Work Assignment items.
 */
const isPastActivity = (activity) => {
	if (!activity.toTimestamp) return false;
	return new Date(activity.toTimestamp) < new Date();
};

const handleWorkAssignmentActions = (activity, callbacks) => {
	const actions = [];

	if ([TRAVELLING_ACTIVITY_TYPE, PROTECTION_ACTIVITY_TYPE].includes(activity.activityType)) {
		if (isPastActivity(activity)) {
			actions.push({
				id: 'manual-reconciliation-extras',
				label: 'Manual reconciliation',
				action: () =>
					callbacks.onManualReconciliation({
						...activity,
					}),
			});
		}

		actions.push({
			id: 'delete-wa-stub',
			label: 'Cancel activity',
			action: () =>
				callbacks.onDeleteWAActivity({
					...activity,
				}),
		});
	}

	return actions;
};

/**
 * Handler for Break items.
 */
const handleBreakActions = (activity, callbacks) => {
	return [
		{
			id: 'update-break-stub',
			label: 'Edit Break (Stub)',
			action: () =>
				callbacks.onUpdateBreak({
					breakType: activity.breakType,
				}),
		},
	];
};

/**
 * Strategy Map
 */
const ACTION_STRATEGIES = {
	[ACTIVITY_GRAHQL_TYPES.ABSENCE]: handleAbsenceActions,
	[ACTIVITY_GRAHQL_TYPES.WORK_ASSIGNMENT]: handleWorkAssignmentActions,
	[ACTIVITY_GRAHQL_TYPES.BREAK]: handleBreakActions,
};

/**
 * MAIN FACTORY
 * @param {Object} rawActivity - The raw 'activity' object from GQL
 * @param {Object} callbacks - Dictionary of function implementations
 */
export const getActivityActions = (rawActivity, callbacks) => {
	if (!rawActivity || !rawActivity.__typename) return [];

	const strategy = ACTION_STRATEGIES[rawActivity.__typename];

	if (strategy) {
		return strategy(rawActivity, callbacks);
	}

	return [];
};
